import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  History,
  ShieldCheck,
} from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import {
  buildFramingStageViews,
  type FramingBox,
  type FramingSceneInspector,
  type FramingStageKey,
  verifyFramingEvidence,
} from "./framingInspector";
import { FramingContactSheets } from "./FramingContactSheets";
import type { FramingOverlayLayer } from "./FramingOverlay";
import { cropPathDuration } from "./framingOverlayMath";
import { FramingStageViewer } from "./FramingStageViewer";
import { formatDateTime } from "./sessionUtils";

const LAYERS: ReadonlyArray<{
  key: FramingOverlayLayer;
  label: string;
  color: string;
}> = [
  { key: "observations", label: "Observed subjects", color: "#22d3ee" },
  {
    key: "delivered_semantic",
    label: "Delivered semantic verification",
    color: "#4ade80",
  },
  { key: "protected", label: "Protected subjects", color: "#facc15" },
  {
    key: "source_transform",
    label: "Source / provider transform",
    color: "#f43f5e",
  },
  {
    key: "visible_content",
    label: "Visible source pixels / padding",
    color: "#38bdf8",
  },
  {
    key: "planned_crop",
    label: "Plan / proposed crop path",
    color: "#fb923c",
  },
  {
    key: "safe_zone",
    label: "Provider / delivery safe zones",
    color: "#a78bfa",
  },
  { key: "applied_crop", label: "Applied crop / path", color: "#4ade80" },
  { key: "effect_viewport", label: "Effect viewport", color: "#f472b6" },
] as const;

const DEFAULT_LAYERS = new Set<FramingOverlayLayer>(
  LAYERS.map((layer) => layer.key),
);

function shortHash(value?: string | null): string {
  if (!value) return "n/a";
  return value.length > 16 ? `${value.slice(0, 12)}…${value.slice(-4)}` : value;
}

function humanize(value?: string | null): string {
  return value?.replaceAll("_", " ") ?? "n/a";
}

function dimensions(width?: number | null, height?: number | null): string {
  return width && height ? `${width} × ${height}` : "unknown dimensions";
}

function boxCoordinates(box?: FramingBox | null): string {
  return box
    ? `[${box.xMin.toFixed(4)}, ${box.yMin.toFixed(4)}, ${box.xMax.toFixed(4)}, ${box.yMax.toFixed(4)}]`
    : "not recorded";
}

export function FramingCoordinateInspector({
  inspector,
}: {
  inspector: FramingSceneInspector;
}) {
  const stages = useMemo(() => buildFramingStageViews(inspector), [inspector]);
  const [selectedStage, setSelectedStage] = useState(stages[0]?.key ?? "raw");
  const [visibleLayers, setVisibleLayers] = useState(DEFAULT_LAYERS);
  const [timeSeconds, setTimeSeconds] = useState(0);
  const verification = useMemo(
    () => verifyFramingEvidence(inspector),
    [inspector],
  );
  const activeStage =
    stages.find((stage) => stage.key === selectedStage) ?? stages[0] ?? null;
  const duration = Math.max(
    cropPathDuration(inspector.decision?.cropPath ?? []),
    cropPathDuration(inspector.appliedReceipt?.cropPath ?? []),
    ...inspector.observations.map(
      (observation) => observation.timestampSeconds ?? 0,
    ),
    ...inspector.coordinateSpaces.map((space) =>
      space.timelineEndSeconds != null
        ? Math.max(
            0,
            space.timelineEndSeconds - (space.timelineStartSeconds ?? 0),
          )
        : 0,
    ),
  );
  const clampedTimeSeconds = Math.min(Math.max(0, timeSeconds), duration);
  const latestQc = inspector.qcHistory.at(-1) ?? null;
  const receipt = inspector.appliedReceipt;
  const identityRequired =
    inspector.intent?.subjects.some((subject) => subject.identityRequired) ??
    false;
  const identityVerified = inspector.observations.some((observation) =>
    ["verified", "matched", "passed"].includes(
      observation.identityStatus?.toLowerCase() ?? "",
    ),
  );
  const identityStatusRecorded = inspector.observations.some(
    (observation) =>
      observation.identityStatus != null || observation.identityScore != null,
  );
  const observationSpaceCount = new Set(
    inspector.observations.map((observation) => observation.coordinateSpaceId),
  ).size;

  const toggleLayer = (layer: FramingOverlayLayer, checked: boolean) => {
    setVisibleLayers((current) => {
      const next = new Set(current);
      if (checked) next.add(layer);
      else next.delete(layer);
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="framing-coordinate-inspector">
      <div className="grid gap-3 rounded-md border bg-muted/20 p-3 text-sm md:grid-cols-2 xl:grid-cols-4">
        <div>
          <div className="text-xs text-muted-foreground">Current attempt</div>
          <div className="mt-1 font-mono text-xs">
            {inspector.attemptId ??
              inspector.current.attemptId ??
              "not recorded"}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {inspector.sceneType ?? humanize(inspector.sceneKind)}
            {` / ${inspector.evidenceMode === "proposed" ? "proposed / observe" : inspector.evidenceMode}`}
          </div>
          {inspector.sourceSceneType &&
          inspector.finalSceneType &&
          inspector.sourceSceneType !== inspector.finalSceneType ? (
            <div className="mt-1 text-[11px] text-muted-foreground">
              route {inspector.sourceSceneType} -&gt; {inspector.finalSceneType}
            </div>
          ) : null}
          <div className="mt-1 font-mono text-[11px] text-muted-foreground">
            {inspector.providerTaskExpected === true
              ? inspector.current.taskId
                ? `provider task ${inspector.current.taskId}`
                : "provider task missing"
              : inspector.providerTaskExpected === false
                ? inspector.sceneKind === "i2v"
                  ? "I2V-origin fallback / no provider task expected"
                  : "provider task / internal receipt: not applicable"
                : "provider applicability not recorded"}
          </div>
          {inspector.providerTaskExpected === true ? (
            <div className="mt-1 text-[11px] text-muted-foreground">
              {inspector.providerInputReceipt
                ? `provider receipt ${inspector.providerInputReceipt.schemaVersion ?? "schema unknown"}`
                : inspector.providerReceiptExpected === true
                  ? "provider input receipt missing"
                  : inspector.providerReceiptExpected === false
                    ? "provider-internal crop receipt: not applicable"
                    : "provider receipt expectation not recorded"}
            </div>
          ) : null}
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Plan</div>
          <div className="mt-1 font-mono text-xs">
            {inspector.current.planId ?? receipt?.planId ?? "n/a"}
            {inspector.current.planRevision != null
              ? ` · revision ${inspector.current.planRevision}`
              : ""}
          </div>
          <div className="mt-1 font-mono text-[11px] text-muted-foreground">
            {shortHash(inspector.current.planDigest ?? receipt?.planDigest)}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">Applied receipt</div>
          <div className="mt-1 font-mono text-xs">
            {receipt?.receiptId ??
              inspector.current.receiptId ??
              (inspector.evidenceMode === "proposed"
                ? "not applicable"
                : "missing")}
          </div>
          <div className="mt-1 text-[11px] text-muted-foreground">
            {receipt
              ? `${humanize(receipt.strategy)} · ${receipt.renderer ?? "renderer unknown"}`
              : inspector.evidenceMode === "proposed"
                ? "Observe-only plan was not sent to the renderer"
                : "No worker proof"}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">
            Frozen runtime config
          </div>
          <div className="mt-1 font-mono text-xs">
            {inspector.current.configReleaseId ?? "not recorded"}
          </div>
          <div className="mt-1 font-mono text-[11px] text-muted-foreground">
            {shortHash(inspector.current.configDigest)}
            {inspector.current.configSchemaVersion
              ? ` · schema ${inspector.current.configSchemaVersion}`
              : ""}
          </div>
        </div>
      </div>

      <details className="rounded-md border p-3 text-xs">
        <summary className="cursor-pointer font-medium">
          Exact normalized geometry and coordinate bases
        </summary>
        <dl className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
          <div>
            <dt className="text-muted-foreground">Plan source space</dt>
            <dd className="break-all font-mono">
              {inspector.decision?.coordinateSpaceId ?? "not recorded"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Planned crop window</dt>
            <dd className="font-mono">
              {boxCoordinates(inspector.decision?.cropWindow)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Protected region</dt>
            <dd className="font-mono">
              {boxCoordinates(inspector.decision?.protectedRegion)}
            </dd>
          </div>
          {(inspector.decision?.safeZones.length ?? 0) > 0 ? (
            inspector.decision?.safeZones.map((zone, index) => (
              <div key={`${zone.kind}-${zone.coordinateSpaceId}-${index}`}>
                <dt className="text-muted-foreground">
                  {humanize(zone.kind)} safe zone
                </dt>
                <dd className="break-all font-mono">
                  {zone.coordinateSpaceId ?? "coordinate space not recorded"}
                  {` · ${boxCoordinates(zone.box)}`}
                </dd>
              </div>
            ))
          ) : (
            <div>
              <dt className="text-muted-foreground">Delivery safe zone</dt>
              <dd className="font-mono">not recorded</dd>
            </div>
          )}
          <div>
            <dt className="text-muted-foreground">Visible-content box</dt>
            <dd className="font-mono">
              {boxCoordinates(inspector.decision?.visibleContentBox)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Applied crop window</dt>
            <dd className="font-mono">
              {boxCoordinates(inspector.appliedReceipt?.cropWindow)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">
              Provider normalized crop basis
            </dt>
            <dd className="break-all font-mono">
              {inspector.providerInputReceipt?.cropWindowNormalizedSpace ??
                "not applicable / not recorded"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Provider normalized crop</dt>
            <dd className="font-mono">
              {boxCoordinates(inspector.providerInputReceipt?.cropWindow)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Provider pixel crop basis</dt>
            <dd className="break-all font-mono">
              {inspector.providerInputReceipt?.cropWindowPixelsSpace ??
                "not applicable / not recorded"}
              {inspector.providerInputReceipt?.cropWindowPixels.length
                ? ` · [${inspector.providerInputReceipt.cropWindowPixels.join(", ")}]`
                : ""}
            </dd>
          </div>
        </dl>
      </details>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-md border p-3 text-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Intended by the director
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(inspector.intent?.subjects.length ?? 0) > 0 ? (
              inspector.intent?.subjects.map((subject, index) => (
                <Badge
                  key={subject.subjectId ?? subject.label ?? index}
                  variant={
                    subject.priority === "must_keep" ? "default" : "outline"
                  }
                >
                  {subject.label ?? subject.kind ?? "subject"}
                  {subject.kind ? ` · ${humanize(subject.kind)}` : ""}
                  {subject.priority ? ` · ${humanize(subject.priority)}` : ""}
                  {subject.identityRequired ? " / identity required" : ""}
                  {subject.declarationReason
                    ? ` · ${humanize(subject.declarationReason)}`
                    : ""}
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">
                No semantic subject intent was recorded.
              </span>
            )}
          </div>
          {(inspector.intent?.subjects.length ?? 0) > 0 ? (
            <p className="mt-2 break-all font-mono text-[10px] text-muted-foreground">
              target ids:{" "}
              {inspector.intent?.subjects
                .map((subject) => subject.subjectId ?? "unidentified-subject")
                .join(", ")}
            </p>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">
            {[inspector.intent?.mode, inspector.intent?.source]
              .filter(Boolean)
              .map(humanize)
              .join(" · ") || "automatic / legacy"}
          </p>
          {inspector.intent ? (
            <dl className="mt-3 grid gap-1 text-xs sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground">Requested shot</dt>
                <dd>{humanize(inspector.intent.shotSize)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Crop / tracking</dt>
                <dd>
                  {inspector.intent.cropAllowed == null
                    ? "crop unknown"
                    : inspector.intent.cropAllowed
                      ? "crop allowed"
                      : "crop forbidden"}
                  {" · "}
                  {inspector.intent.trackingAllowed == null
                    ? "tracking unknown"
                    : inspector.intent.trackingAllowed
                      ? "tracking allowed"
                      : "tracking forbidden"}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground">Fallback order</dt>
                <dd>
                  {inspector.intent.fallbackPolicy.length > 0
                    ? inspector.intent.fallbackPolicy.map(humanize).join(" → ")
                    : "not recorded"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">Intent confidence</dt>
                <dd>
                  {inspector.intent.confidence == null
                    ? "not recorded"
                    : `${Math.round(inspector.intent.confidence * 100)}%`}
                </dd>
              </div>
            </dl>
          ) : null}
        </div>
        <div className="rounded-md border p-3 text-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Observed in pixels
          </div>
          <p className="mt-2">
            {inspector.observations.length} observation
            {inspector.observations.length === 1 ? "" : "s"} across{" "}
            {observationSpaceCount} coordinate space
            {observationSpaceCount === 1 ? "" : "s"}.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {identityRequired
              ? identityVerified
                ? "The retained evidence records identity as verified; consult delivered-pixel QC for final acceptance."
                : identityStatusRecorded
                  ? "Identity evidence was evaluated but is not recorded as verified."
                  : "Identity was required but no verification result was retained."
              : "Identity verification was not required."}
          </p>
        </div>
      </div>

      {verification.status === "proposed" ? (
        <Alert className="border-blue-300 bg-blue-50 text-blue-950 dark:bg-blue-950/30 dark:text-blue-100">
          <ShieldCheck />
          <AlertTitle>
            Proposed enforcement geometry, not applied proof
          </AlertTitle>
          <AlertDescription>
            <p className="mb-2">
              Orange geometry predicts enforcement. Green receipt geometry and
              actual-applied stages describe pixels that were really rendered;
              proposed overlays are not drawn on actual-only assets.
            </p>
            <ul className="list-disc pl-4">
              {verification.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : verification.status === "mismatch" ? (
        <Alert variant="destructive">
          <AlertTriangle />
          <AlertTitle>
            Planned and applied framing do not fully agree
          </AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {verification.issues.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : verification.status === "unknown" ? (
        <Alert className="border-amber-300 bg-amber-50 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100">
          <AlertTriangle />
          <AlertTitle>
            Evidence is incomplete; framing is not verified
          </AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-4">
              {verification.issues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : (
        <Alert>
          <CheckCircle2 className="text-emerald-600" />
          <AlertTitle>
            Plan, renderer receipt, and delivered-pixel QC are verified
          </AlertTitle>
          <AlertDescription>
            Asset identity, source dimensions, crop path and timestamps, effect
            viewport, output identity, and QC agree within one pixel and one
            video-frame tolerance.
          </AlertDescription>
        </Alert>
      )}

      {stages.length > 0 && activeStage ? (
        <Tabs
          value={activeStage.key}
          onValueChange={(value) => setSelectedStage(value as FramingStageKey)}
        >
          <div className="overflow-x-auto pb-1">
            <TabsList aria-label="Framing coordinate stages">
              {stages.map((stage) => (
                <TabsTrigger key={stage.key} value={stage.key}>
                  {stage.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {stages.map((stage) => (
            <TabsContent key={stage.key} value={stage.key}>
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {stage.description}
                </p>
                <FramingStageViewer
                  label={stage.label}
                  coordinateSpace={stage.coordinateSpace}
                  coordinateSpaces={inspector.coordinateSpaces}
                  artifact={stage.artifact}
                  observations={inspector.observations}
                  deliveredEvidence={latestQc?.deliveredSubjectEvidence ?? []}
                  decision={inspector.decision}
                  receipt={receipt}
                  evidenceMode={inspector.evidenceMode}
                  visibleLayers={visibleLayers}
                  timeSeconds={clampedTimeSeconds}
                  onTimeChange={setTimeSeconds}
                />
                <dl className="grid gap-2 rounded-md border p-3 text-xs sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    <dt className="text-muted-foreground">Coordinate space</dt>
                    <dd className="mt-1 break-all font-mono">
                      {stage.coordinateSpace?.id ?? "not recorded"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Intrinsic pixels</dt>
                    <dd className="mt-1 font-mono">
                      {dimensions(
                        stage.coordinateSpace?.width,
                        stage.coordinateSpace?.height,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Asset hash</dt>
                    <dd className="mt-1 break-all font-mono">
                      {shortHash(
                        stage.coordinateSpace?.assetHash ??
                          stage.artifact?.sha256,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">
                      {stage.coordinateSpace?.retentionState === "retained"
                        ? "Exact retained storage key"
                        : "Lineage locator (not retained)"}
                    </dt>
                    <dd className="mt-1 break-all font-mono">
                      {stage.coordinateSpace?.assetKey ??
                        stage.artifact?.key ??
                        "not retained"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Evidence role</dt>
                    <dd className="mt-1">
                      {humanize(stage.coordinateSpace?.evidenceRole)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Retention</dt>
                    <dd className="mt-1">
                      {humanize(stage.coordinateSpace?.retentionState)}
                      {stage.coordinateSpace?.viewable === false
                        ? " · metadata only"
                        : stage.coordinateSpace?.viewable === true
                          ? " · viewable"
                          : ""}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-muted-foreground">Timeline range</dt>
                    <dd className="mt-1 font-mono">
                      {stage.coordinateSpace?.timelineStartSeconds != null
                        ? `${stage.coordinateSpace.timelineStartSeconds.toFixed(3)}s → ${(
                            stage.coordinateSpace.timelineEndSeconds ??
                            stage.coordinateSpace.timelineStartSeconds
                          ).toFixed(3)}s`
                        : "independent / still"}
                    </dd>
                  </div>
                </dl>
                {stage.coordinateSpace?.parentSpaceId ? (
                  <p className="break-all text-xs text-muted-foreground">
                    Parent {stage.coordinateSpace.parentSpaceId} · transform{" "}
                    {humanize(stage.coordinateSpace.transformFromParent?.kind)}
                    {stage.coordinateSpace.transformFromParent?.orientation
                      ? ` · ${humanize(stage.coordinateSpace.transformFromParent.orientation)}`
                      : ""}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Independent coordinate root
                  </p>
                )}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      ) : (
        <Alert>
          <AlertTriangle />
          <AlertTitle>No coordinate-stage assets are available</AlertTitle>
          <AlertDescription>
            This is a v1/event-only scene. The summary and immutable event
            history below remain available.
          </AlertDescription>
        </Alert>
      )}

      <FramingContactSheets
        artifacts={inspector.artifacts}
        attemptId={inspector.attemptId ?? inspector.current.attemptId ?? null}
        taskId={inspector.current.taskId ?? null}
        planDigest={inspector.current.planDigest ?? null}
      />

      <details className="rounded-md border p-3 text-sm">
        <summary className="cursor-pointer font-medium">
          Complete coordinate-space lineage ({inspector.coordinateSpaces.length}
          )
        </summary>
        <div className="mt-3 grid gap-2 md:grid-cols-2">
          {inspector.coordinateSpaces.map((space) => (
            <div key={space.id} className="rounded bg-muted/40 p-2 text-xs">
              <div className="flex flex-wrap items-center gap-1.5">
                <Badge variant="outline">{humanize(space.stage)}</Badge>
                <Badge variant="outline">
                  {humanize(space.retentionState)}
                </Badge>
                <Badge variant="outline">{humanize(space.evidenceRole)}</Badge>
              </div>
              <div className="mt-1 break-all font-mono">{space.id}</div>
              <div className="mt-1 text-muted-foreground">
                {dimensions(space.width, space.height)}
                {space.parentSpaceId
                  ? ` · parent ${space.parentSpaceId}`
                  : " · root"}
              </div>
              <div className="mt-1 break-all font-mono text-[10px] text-muted-foreground">
                {space.retentionState === "retained"
                  ? "retained key"
                  : "lineage locator"}
                : {space.assetKey ?? "not recorded"}
              </div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Ephemeral and external locators describe lineage only; they are not a
          promise that the underlying bytes can be fetched.
        </p>
      </details>

      <fieldset className="rounded-md border p-3">
        <legend className="px-1 text-sm font-medium">Overlay layers</legend>
        <div className="mt-1 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {LAYERS.map((layer) => (
            <label
              key={layer.key}
              className="flex cursor-pointer items-center gap-2 text-sm"
            >
              <Checkbox
                checked={visibleLayers.has(layer.key)}
                onCheckedChange={(checked) =>
                  toggleLayer(layer.key, checked === true)
                }
                aria-label={`Show ${layer.label.toLowerCase()}`}
              />
              <span
                className="size-2.5 rounded-sm border"
                style={{ backgroundColor: layer.color }}
                aria-hidden="true"
              />
              {layer.label}
            </label>
          ))}
        </div>
      </fieldset>

      {duration > 0 ? (
        <label className="block rounded-md border p-3 text-sm">
          <span className="flex items-center justify-between gap-2">
            <span className="font-medium">Crop-path time</span>
            <span className="font-mono text-xs">
              {clampedTimeSeconds.toFixed(2)}s / {duration.toFixed(2)}s
            </span>
          </span>
          <input
            type="range"
            min={0}
            max={duration}
            step={0.04}
            value={clampedTimeSeconds}
            onChange={(event) => setTimeSeconds(Number(event.target.value))}
            className="mt-2 w-full accent-primary"
            aria-label="Crop-path time"
          />
        </label>
      ) : null}

      <div className="space-y-2 rounded-md border p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4 className="text-sm font-medium">Observed subjects</h4>
          <Badge variant="outline">{inspector.observations.length}</Badge>
        </div>
        {inspector.observations.length > 0 ? (
          <div className="grid gap-2 md:grid-cols-2">
            {inspector.observations.map((observation, index) => (
              <div
                key={`${observation.coordinateSpaceId}-${observation.subjectId ?? index}`}
                className="rounded bg-muted/50 p-2 text-xs"
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-medium">
                    {observation.label ?? observation.kind ?? "Unknown subject"}
                  </span>
                  {observation.priority ? (
                    <Badge variant="outline" className="text-[10px]">
                      {humanize(observation.priority)}
                    </Badge>
                  ) : null}
                  {inspector.decision?.protectedSubjectIds.includes(
                    observation.subjectId ?? "",
                  ) ? (
                    <Badge className="text-[10px]">protected</Badge>
                  ) : null}
                </div>
                <div className="mt-1 text-muted-foreground">
                  {observation.detector ?? "detector unknown"}
                  {observation.confidence != null
                    ? ` · ${Math.round(observation.confidence * 100)}%`
                    : ""}
                  {observation.associationMethod
                    ? ` · ${humanize(observation.associationMethod)}`
                    : ""}
                  {observation.associationReasonCode
                    ? ` · ${humanize(observation.associationReasonCode)}`
                    : ""}
                </div>
                <div className="mt-1 text-muted-foreground">
                  {observation.intentSource
                    ? `intent ${humanize(observation.intentSource)}`
                    : "intent source not recorded"}
                  {observation.declarationReason
                    ? ` · ${humanize(observation.declarationReason)}`
                    : ""}
                  {observation.identityStatus
                    ? ` · identity ${humanize(observation.identityStatus)}`
                    : " · identity status not recorded"}
                </div>
                <div className="mt-1 break-all font-mono text-[10px] text-muted-foreground">
                  {observation.coordinateSpaceId} · [
                  {observation.bbox.xMin.toFixed(3)},{" "}
                  {observation.bbox.yMin.toFixed(3)},{" "}
                  {observation.bbox.xMax.toFixed(3)},{" "}
                  {observation.bbox.yMax.toFixed(3)}]
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">
            No coordinate observations were retained for this attempt.
          </p>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div className="rounded-md border p-3 text-sm">
          <h4 className="font-medium">Planned versus applied</h4>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Planned</dt>
            <dd>{humanize(inspector.decision?.strategy)}</dd>
            <dt className="text-muted-foreground">Applied</dt>
            <dd>{humanize(receipt?.strategy)}</dd>
            <dt className="text-muted-foreground">Source hash</dt>
            <dd className="break-all font-mono">
              {shortHash(receipt?.sourceAssetHash)}
            </dd>
            <dt className="text-muted-foreground">Output hash</dt>
            <dd className="break-all font-mono">
              {shortHash(receipt?.outputAssetHash)}
            </dd>
            <dt className="text-muted-foreground">Applied at</dt>
            <dd>{formatDateTime(receipt?.appliedAt)}</dd>
          </dl>
        </div>

        <div className="rounded-md border p-3 text-sm">
          <h4 className="font-medium">Latest delivered-pixel QC</h4>
          {latestQc ? (
            <div className="mt-2 space-y-1 text-xs">
              <div className="flex flex-wrap items-center gap-2">
                <Badge
                  variant={
                    latestQc.status === "failed" ? "destructive" : "outline"
                  }
                >
                  {latestQc.status ?? "unknown"}
                </Badge>
                <span className="font-mono text-muted-foreground">
                  {latestQc.checkedCoordinateSpaceId ?? "space not recorded"}
                </span>
              </div>
              {latestQc.issueCodes.length > 0 ? (
                <p>{latestQc.issueCodes.map(humanize).join(" · ")}</p>
              ) : (
                <p className="text-muted-foreground">No issue codes.</p>
              )}
              <p className="text-muted-foreground">
                {latestQc.sampleCount ?? 0} samples · repair{" "}
                {latestQc.repairAttempt ?? 0}
              </p>
              <p className="text-muted-foreground">
                Semantic verifier: {humanize(latestQc.semanticVerdict)} ·{" "}
                {latestQc.deliveredSubjectEvidence.length} delivered boxes
              </p>
            </div>
          ) : (
            <p className="mt-2 text-xs text-muted-foreground">
              No delivered-pixel QC record exists.
            </p>
          )}
        </div>
      </div>

      {latestQc?.deliveredSubjectEvidence.length ? (
        <section className="space-y-2 rounded-md border p-3 text-sm">
          <h4 className="font-medium">Delivered-pixel semantic evidence</h4>
          <p className="text-xs text-muted-foreground">
            These boxes were detected again on encoded scene pixels. They are
            distinct from source or generated-clip lineage observations.
          </p>
          <div className="grid gap-2 md:grid-cols-2">
            {latestQc.deliveredSubjectEvidence.map((evidence, index) => (
              <div
                key={`${evidence.subjectId}-${evidence.sampleIndex}-${index}`}
                className="rounded bg-muted/40 p-2 text-xs"
              >
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge
                    variant={
                      evidence.matchStatus === "wrong_subject"
                        ? "destructive"
                        : "outline"
                    }
                  >
                    {humanize(evidence.matchStatus)}
                  </Badge>
                  <span>{humanize(evidence.kind)}</span>
                  <span className="text-muted-foreground">
                    {evidence.confidence == null
                      ? "confidence not recorded"
                      : `${Math.round(evidence.confidence * 100)}%`}
                  </span>
                </div>
                <div className="mt-1 break-all font-mono">
                  {evidence.coordinateSpaceId ?? "space not recorded"} ·{" "}
                  {boxCoordinates(evidence.bbox)}
                </div>
                <div className="mt-1 text-muted-foreground">
                  scene {evidence.sceneTimestampSeconds?.toFixed(3) ?? "?"}s ·
                  delivery{" "}
                  {evidence.deliveryTimestampSeconds?.toFixed(3) ?? "?"}s ·{" "}
                  {evidence.model ?? evidence.detector ?? "model not recorded"}
                </div>
              </div>
            ))}
          </div>
        </section>
      ) : null}

      <details className="rounded-md border p-3 text-sm">
        <summary className="cursor-pointer font-medium">
          <History className="mr-1.5 inline size-4" />
          Current attempt and immutable history ({inspector.history.length})
        </summary>
        <div className="mt-3 space-y-2 border-l pl-3">
          {inspector.history.length > 0 ? (
            inspector.history.map((event) => (
              <div key={event.id} className="text-xs">
                <div className="font-medium">
                  {event.eventType.replaceAll(".", " › ")}
                </div>
                <div className="text-muted-foreground">
                  {formatDateTime(event.occurredAt)}
                  {event.planRevision != null
                    ? ` · plan revision ${event.planRevision}`
                    : ""}
                  {event.attemptId ? ` · attempt ${event.attemptId}` : ""}
                </div>
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground">
              The current projection is available, but no historical events were
              returned.
            </p>
          )}
        </div>
      </details>

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" />
        Diagnostic pixels use short-lived private URLs. The inspector never
        receives embeddings, raw model responses, or unrelated interview text.
      </p>
    </div>
  );
}
